import React, {
  forwardRef,
  useState,
  useImperativeHandle,
  useCallback,
  useEffect,
} from "react";
import { Col, Row } from "react-bootstrap";
import classes from "@/Modals/BaseModal.module.css";
import { useFormik } from "formik";
import ModalSkeleton from "@/components/ModalSkeleton";
import Button from "@/components/Button";
import { usePost } from "@/apiHooks/HttpFunctions";
import {
  productCategoriesMasterDataApi,
  productGroupsMasterDataApi,
  productsMasterDataApi,
  SportGroupCategoryProductMappingsApi,
  sportsMasterDataApi,
} from "@/apis/apis";
import RenderToast from "@/components/RenderToast";
import { LOADING_PLACEHOLDER } from "@/constant/constants";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import SportGroupCategoryProductInitialValues from "@/data/WebDataManagement/Mapping/SportGroupCategoryProduct/initial-values";
import SportGroupCategoryProductSchema from "@/schema/WebDataManagement/Mapping/SportGroupCategoryProduct/SportGroupCategoryProductSchema";

const SportGroupCategoryProductModal = forwardRef((_, ref) => {
  const [saveError, setSaveError] = useState(null);
  const [modalState, setModalState] = useState({ isOpen: false });

  const handleSubmit = (values) => {
    const payload = {
      sport_id: values.sport_id,
      product_group_id: values.product_group_id,
      parent_product_category_id: values.parent_product_category_id,
      product_category_id: values.product_category_id,
      product_id: values.product_id,
    };
    saveAdd(JSON.stringify(payload));
  };

  const formik = useFormik({
    initialValues: SportGroupCategoryProductInitialValues(),
    validationSchema: SportGroupCategoryProductSchema(),
    onSubmit: handleSubmit,
  });

  const resetError = useCallback(() => {
    setSaveError(null);
  }, []);

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [formik, resetError]);

  const handleSuccess = useCallback(() => {
    closeModal();
    RenderToast({
      type: "success",
      message: "Sport - Group - Category - Product added successfully.",
    });
  }, [closeModal]);

  const {
    mutate: saveAdd,
    isPending: saveIsPending,
    error: saveAddError,
  } = usePost({
    apiURL: `${SportGroupCategoryProductMappingsApi}`,
    onSuccess: handleSuccess,
  });

  useEffect(() => {
    setSaveError(saveAddError);
  }, [saveAddError]);

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const perPage = LIST_PER_PAGE;
  const queryParams = `?mode=${LIST_MODE.SELECT}&perPage=${perPage}&filter[keyword]=`;
  const sportListApi = `${sportsMasterDataApi}${queryParams}`;
  const {
    loading: sportLoading,
    defaultOptions: sportDefaultOptions,
    debouncedFetchOptions: sportFetchOptions,
    getSelectedOption: sportGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: sportListApi,
    formikValue: formik.values.sport_id,
    formikInitialValue: formik.initialValues.sport_id,
  });

  const productGroupListApi = `${productGroupsMasterDataApi}${queryParams}`;
  const {
    loading: productGroupLoading,
    defaultOptions: productGroupDefaultOptions,
    debouncedFetchOptions: productGroupFetchOptions,
    getSelectedOption: productGroupGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: productGroupListApi,
    formikValue: formik.values.product_group_id,
    formikInitialValue: formik.initialValues.product_group_id,
  });

  const productCategoryListApi = `${productCategoriesMasterDataApi}${queryParams}`;
  const {
    loading: productCategoryLoading,
    defaultOptions: productCategoryDefaultOptions,
    debouncedFetchOptions: productCategoryFetchOptions,
    getSelectedOption: productCategoryGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: productCategoryListApi,
    formikValue: formik.values.product_category_id,
    formikInitialValue: formik.initialValues.product_category_id,
  });

  const {
    loading: parentProductCategoryLoading,
    defaultOptions: parentProductCategoryDefaultOptions,
    debouncedFetchOptions: parentProductCategoryFetchOptions,
    getSelectedOption: parentProductCategoryGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: productCategoryListApi,
    formikValue: formik.values.parent_product_category_id,
    formikInitialValue: formik.initialValues.parent_product_category_id,
  });

  const productListApi = `${productsMasterDataApi}${queryParams}`;
  const {
    loading: productLoading,
    defaultOptions: productDefaultOptions,
    debouncedFetchOptions: productFetchOptions,
    getSelectedOption: productGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: productListApi,
    formikValue: formik.values.product_id,
    formikInitialValue: formik.initialValues.product_id,
  });

  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <ModalSkeleton
      header="Add Sport - Group - Category - Product"
      onHide={closeModal}
      show={modalState.isOpen}
      modalStyles={classes.modal}
      showCloseIcon={true}
      showLogo={true}
    >
      <form
        onSubmit={formik.handleSubmit}
        className="h-[50vh] overflow-hidden flex gap-y-2 flex-col"
      >
        <div className="flex-1 flex pe-4 flex-col gap-y-2 overflow-y-auto">
          <Row>
            <Col md={12}>
              <AsyncDropdown
                label="Sport"
                placeholder="No sport selected. Type to search..."
                defaultOptions={sportDefaultOptions}
                loadOptions={sportFetchOptions}
                isLoading={sportLoading}
                value={sportGetSelectedOption()}
                onChange={(selected) => {
                  formik.setFieldValue("sport_id", selected?.value || null);
                }}
                errorText={
                  (formik.touched.sport_id && formik.errors.sport_id) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.sport_id?.join(" "))
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <AsyncDropdown
                label="Product Group"
                placeholder="No product group selected. Type to search..."
                defaultOptions={productGroupDefaultOptions}
                loadOptions={productGroupFetchOptions}
                isLoading={productGroupLoading}
                value={productGroupGetSelectedOption()}
                onChange={(selected) => {
                  formik.setFieldValue(
                    "product_group_id",
                    selected?.value || null
                  );
                }}
                errorText={
                  (formik.touched.product_group_id &&
                    formik.errors.product_group_id) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.product_group_id?.join(
                      " "
                    ))
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <AsyncDropdown
                required={false}
                label="Parent Product Category"
                placeholder="No product category selected. Type to search..."
                defaultOptions={parentProductCategoryDefaultOptions}
                loadOptions={parentProductCategoryFetchOptions}
                isLoading={parentProductCategoryLoading}
                value={parentProductCategoryGetSelectedOption()}
                onChange={(selected) => {
                  formik.setFieldValue(
                    "parent_product_category_id",
                    selected?.value || null
                  );
                }}
                errorText={
                  (formik.touched.parent_product_category_id &&
                    formik.errors.parent_product_category_id) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.parent_product_category_id?.join(
                      " "
                    ))
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <AsyncDropdown
                required={false}
                label="Product Category"
                placeholder="No product category selected. Type to search..."
                defaultOptions={productCategoryDefaultOptions}
                loadOptions={productCategoryFetchOptions}
                isLoading={productCategoryLoading}
                value={productCategoryGetSelectedOption()}
                onChange={(selected) => {
                  formik.setFieldValue(
                    "product_category_id",
                    selected?.value || null
                  );
                }}
                errorText={
                  (formik.touched.product_category_id &&
                    formik.errors.product_category_id) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.product_category_id?.join(
                      " "
                    ))
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <AsyncDropdown
                label="Product"
                placeholder="No product selected. Type to search..."
                defaultOptions={productDefaultOptions}
                loadOptions={productFetchOptions}
                isLoading={productLoading}
                value={productGetSelectedOption()}
                onChange={(selected) => {
                  formik.setFieldValue("product_id", selected?.value || null);
                }}
                errorText={
                  (formik.touched.product_id && formik.errors.product_id) ||
                  (saveError?.status === 422 &&
                    saveError?.response?.data?.errors?.product_id?.join(" "))
                }
              />
            </Col>
          </Row>
        </div>
        <div>
          <Row className="mt-4">
            <Col md={6}>
              <Button
                variant="outlined"
                label="Cancel"
                onClick={closeModal}
                type="button"
                className={classes.button}
                disabled={saveIsPending}
              />
            </Col>
            <Col md={6}>
              <Button
                id="submit"
                variant="green-gradient"
                label={saveIsPending ? LOADING_PLACEHOLDER : "Save"}
                type="submit"
                className={classes.button}
                disabled={saveIsPending}
              />
            </Col>
          </Row>
        </div>
      </form>
    </ModalSkeleton>
  );
});

export default SportGroupCategoryProductModal;
